import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTranslatedList } from "@/hooks/useTranslatedList";

interface DirectSession {
  id: string;
  title: string;
  status: "live" | "upcoming" | "ended";
  description: string;
  source: string;
  scheduledAt: string;
}

const mockSessions: DirectSession[] = [
  {
    id: "1",
    title: "L+TV Direct: Community Town Hall",
    status: "upcoming",
    description: "Join us for a live community discussion with local leaders and residents. Ask your questions and share your perspectives.",
    source: "Direct Stream",
    scheduledAt: "2026-09-01T18:00:00Z",
  },
  {
    id: "2",
    title: "L+TV Direct: Youth Innovation Panel",
    status: "ended",
    description: "A special panel discussion featuring young entrepreneurs and innovators from across Cameroon.",
    source: "YouTube Live",
    scheduledAt: "2026-08-20T15:00:00Z",
  },
];

export default function DirectPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"all" | "live" | "upcoming" | "ended">("all");

  useEffect(() => {
    document.title = "L+TV Direct | L+TV TÉLÉVISION";
  }, []);

  const tabs = [
    { value: "all" as const, label: t("direct.tab.all") },
    { value: "live" as const, label: t("direct.tab.live") },
    { value: "upcoming" as const, label: t("direct.tab.upcoming") },
    { value: "ended" as const, label: t("direct.tab.ended") },
  ];

  const filtered = useTranslatedList(
    activeTab === "all" ? mockSessions : mockSessions.filter((s) => s.status === activeTab),
    ["title", "description"],
  );

  const statusLabel = (s: DirectSession["status"]) =>
    s === "live" ? t("direct.tab.live") : s === "upcoming" ? t("direct.tab.upcoming") : t("direct.tab.ended");

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <h1 className="text-2xl md:text-3xl font-bold text-white">{t("direct.title")}</h1>
          <p className="text-white/60 text-sm mt-1">{t("direct.subtitle")}</p>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-foreground-700 leading-relaxed text-lg">
            {t("direct.intro")}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-background-100 rounded-md p-1 mb-8 max-w-md mx-auto">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === tab.value
                  ? "bg-primary-500 text-white"
                  : "text-foreground-600 hover:text-foreground-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Sessions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map((session) => (
            <div key={session.id} className="bg-white rounded-lg border border-background-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                  session.status === "live" ? "bg-accent-500 text-white" :
                  session.status === "upcoming" ? "bg-primary-500 text-white" :
                  "bg-foreground-200 text-foreground-600"
                }`}>
                  {statusLabel(session.status)}
                </span>
                {session.status === "live" && (
                  <span className="ltv-live-badge text-[10px]">
                    <i className="ri-broadcast-fill"></i>
                    {t("direct.liveNow")}
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-foreground-900 mb-2">{session.title}</h3>
              <p className="text-sm text-foreground-600 mb-3">{session.description}</p>
              <div className="flex items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1"><i className="ri-broadcast-line"></i>{session.source}</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{new Date(session.scheduledAt).toLocaleDateString()}</span>
              </div>
              {session.status === "live" && (
                <Link to="/live" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-accent-500 hover:text-accent-600">
                  {t("direct.joinLive")} <i className="ri-arrow-right-line"></i>
                </Link>
              )}
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-broadcast-line text-4xl text-foreground-300 mb-3"></i>
            <p className="text-foreground-500">{t("direct.noSessions")}</p>
          </div>
        )}
      </div>
    </div>
  );
}